import React from "react";
import PageHeader from "../../common/PageHeader";
import { GlobalContext } from "../../context/GlobalContext";
import { fetchInfModels } from "../influencers/infModelsApi";
import { TranslateContext } from "../../context/TranslateContext";
import { mediaSrc } from "../../utils/mediaSrc";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AutoAwesomeOutlined,
  CampaignOutlined,
  ContentCopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  HistoryOutlined,
  PlayArrowOutlined,
  RefreshOutlined,
  ScheduleSendOutlined,
} from "@mui/icons-material";
import {
  ChooseAvatarDialog,
  ChooseVoiceDialog,
} from "../videoAgent/VideoAgentModals";
import ProductAdsInfluencerDialog from "./ProductAdsInfluencerDialog";
import ProductAdsStudioStep from "./ProductAdsStudioStep";
import ProductAdsCategoryField from "./ProductAdsCategoryField";
import CategoryFilterChips from "../../common/CategoryFilterChips";
import { InlineLoader } from "../../LoadingComp";
import {
  PRODUCT_AD_DURATION_OPTIONS,
  PRODUCT_AD_DEFAULT_DURATION_SEC,
  normalizeProductAdDurationSec,
} from "../../constants/productAdDurations";

const STEPS = ["studio", "settings", "preview", "generate"];

function normalizeWizardStep(step) {
  const n = Number(step) || 0;
  if (n <= 3) return Math.min(3, Math.max(0, n));
  if (n === 4) return 1;
  if (n === 5) return 2;
  return 3;
}

function buildPersistPayload(form, productLink, categoryId) {
  const link = String(productLink || "").trim();
  const config = { ...(form.config || {}) };
  if (link) config.product_url = link;
  else delete config.product_url;
  const payload = {
    product_name: form.product_name,
    description: form.description,
    selling_points: form.selling_points,
    cta: form.cta,
    script: form.script,
    config,
  };
  if (categoryId) payload.cate_id = Number(categoryId);
  return payload;
}

function formatGenerationStatus(lang, ad, jobProgress, pollPct) {
  const jp = jobProgress || ad?.job_progress;
  if (jp?.segments?.length) {
    const plan = jp.duration_plan || jp.segments.map((s) => `${s.duration_sec}s`).join(" + ");
    if (jp.phase === "stitching") {
      return lang?.productAdsStatusStitching || `Stitching clips (${plan})… almost there!`;
    }
    if (jp.failed_count > 0) {
      return (
        lang?.productAdsStatusPartialFail ||
        `Clip ${jp.completed_count}/${jp.total_count} ready — retry failed parts to merge`
      );
    }
    const eta =
      jp.estimated_sec_remaining > 0
        ? `~${Math.ceil(jp.estimated_sec_remaining / 60) || 1} min left`
        : "";
    return (
      lang?.productAdsStatusSegments ||
      `Rendering clip ${jp.completed_count + 1} of ${jp.total_count} (${plan}) ${eta}`.trim()
    );
  }
  if (ad?.heygen_status?.includes("segment")) {
    return lang?.productAdsStatusKling || "Building your ad from AI video clips…";
  }
  const pct = jp?.progress_pct ?? pollPct;
  return `${lang?.productAdsGenerating || "Generating your ad…"} ${pct > 0 ? `${Math.round(pct)}%` : ""}`.trim();
}

function adCoverSrc(item, influencers, cfg) {
  if (item?.cover_image) return item.cover_image;
  if (item?.video_thumbnail) return mediaSrc(item.video_thumbnail);
  if (item?.media_url) return mediaSrc(item.media_url);
  const infId = cfg?.influencer_id ?? item?.config?.influencer_id;
  if (infId && influencers?.length) {
    const inf = influencers.find((i) => Number(i.id) === Number(infId));
    if (inf?.photo_url) return mediaSrc(inf.photo_url);
  }
  return null;
}

function computeEstimatedCredits(durationSec, engine, meta) {
  const duration = normalizeProductAdDurationSec(durationSec);
  if (meta?.pricing_mode === "per_second" && Number(meta.credits_per_second) > 0) {
    return Math.max(1, Math.round(duration * Number(meta.credits_per_second)));
  }
  if (engine === "heygen_agent") {
    return Number(meta?.heygen_credit_fee) || 0;
  }
  return Number(meta?.showcase_credit_fee) || 0;
}

const DEFAULT_AD_CONFIG = {
  generation_engine: "veo_showcase",
  influencer_id: null,
  video_format: "ugc_shorts",
  duration_sec: PRODUCT_AD_DEFAULT_DURATION_SEC,
  aspect_ratio: "9:16",
  background_style: "lifestyle",
  music_track: "ugc_upbeat",
  subtitles: true,
  seedance_enabled: true,
  export_quality: "1080p",
  brand_colors: { primary: "#00C853", secondary: "#111827" },
  language: "en",
};

const GENERATION_ENGINES = [
  { id: "veo_showcase", labelKey: "productAdsEngineVeo" },
  { id: "heygen_agent", labelKey: "productAdsEngineHeygen" },
];

const FORMAT_OPTIONS = [
  { id: "ugc_shorts", labelKey: "productAdsFormatUgc" },
  { id: "studio", labelKey: "productAdsFormatStudio" },
];

const DURATION_OPTIONS = PRODUCT_AD_DURATION_OPTIONS;

const ASPECT_OPTIONS = ["9:16", "16:9", "1:1"];
const MUSIC_OPTIONS = [
  { id: "ugc_upbeat", labelKey: "productAdsMusicUgc" },
  { id: "soft_ambient", labelKey: "productAdsMusicSoft" },
  { id: "energetic_pop", labelKey: "productAdsMusicEnergetic" },
  { id: "upbeat_corporate", labelKey: "productAdsMusicCorporate" },
  { id: "none", labelKey: "productAdsMusicNone" },
];
const BG_STYLES = [
  { id: "clean_studio", label: "Clean studio" },
  { id: "lifestyle", label: "Lifestyle" },
  { id: "gradient", label: "Gradient" },
  { id: "outdoor", label: "Outdoor" },
];

function readAdFromRes(res) {
  return res?.data?.data?.ad ?? res?.data?.ad ?? null;
}

function mergeAdConfig(raw) {
  const merged = { ...DEFAULT_AD_CONFIG, ...(raw && typeof raw === "object" ? raw : {}) };
  merged.duration_sec = normalizeProductAdDurationSec(merged.duration_sec);
  return merged;
}

function normalizeHeygenStyle(row) {
  if (!row || typeof row !== "object") return null;
  const id =
    row.style_id != null
      ? String(row.style_id)
      : row.id != null
        ? String(row.id)
        : row.template_id != null
          ? String(row.template_id)
          : "";
  if (!id) return null;
  const name = String(row.name || row.title || row.label || "Style");
  return { id, name };
}

const ProductAds = ({ lang }) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const { hitAxios, showSnack } = React.useContext(GlobalContext);

  const [ads, setAds] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [categoriesLoading, setCategoriesLoading] = React.useState(true);
  const [categoryFilter, setCategoryFilter] = React.useState("all");
  const [categoryId, setCategoryId] = React.useState("");
  const [loadingList, setLoadingList] = React.useState(true);
  const [adId, setAdId] = React.useState(null);
  const [ad, setAd] = React.useState(null);
  const [activeStep, setActiveStep] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [generatingScript, setGeneratingScript] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const [pollPct, setPollPct] = React.useState(0);
  const [jobProgress, setJobProgress] = React.useState(null);
  const [regeneratingSegment, setRegeneratingSegment] = React.useState(null);
  const [refreshingProgress, setRefreshingProgress] = React.useState(false);
  const [versions, setVersions] = React.useState([]);
  const pollBusyRef = React.useRef(false);

  const [productFile, setProductFile] = React.useState(null);
  const [previewUrl, setPreviewUrl] = React.useState(null);

  const [variantCount, setVariantCount] = React.useState(3);
  const [generatingVariants, setGeneratingVariants] = React.useState(false);
  const [variantIds, setVariantIds] = React.useState([]);

  const [voices, setVoices] = React.useState([]);
  const [avatars, setAvatars] = React.useState([]);
  const [agentStyles, setAgentStyles] = React.useState([]);
  const [influencers, setInfluencers] = React.useState([]);
  const [wizardMeta, setWizardMeta] = React.useState({ showcase_available: false });
  const [heygenMetaLoading, setHeygenMetaLoading] = React.useState(false);
  const [stylesLoading, setStylesLoading] = React.useState(false);
  const [influencersLoading, setInfluencersLoading] = React.useState(false);
  const [picker, setPicker] = React.useState(null);
  const [productLink, setProductLink] = React.useState("");

  const [form, setForm] = React.useState({
    product_name: "",
    description: "",
    selling_points: "",
    cta: "Shop now",
    script: "",
    config: {},
  });

  const stepLabels = React.useMemo(
    () =>
      STEPS.map((k) => lang?.[`productAdsStep_${k}`] || k.replace(/([A-Z])/g, " $1")),
    [lang],
  );

  const fetchCategories = React.useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const paths = ["/api/inf/categories", "/api/admin/get_t_care"];
      for (const path of paths) {
        const res = await hitAxios({
          path,
          post: false,
          admin: false,
          showLoading: false,
          showSnackbar: false,
        });
        if (res?.data?.success) {
          setCategories(Array.isArray(res.data.data) ? res.data.data : []);
          return;
        }
        if (res?.response?.status !== 404) break;
      }
    } finally {
      setCategoriesLoading(false);
    }
  }, [hitAxios]);

  const loadList = React.useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await hitAxios({
        path: "/api/product-ads",
        post: false,
        showLoading: false,
      });
      if (res?.data?.success) {
        setAds(Array.isArray(res.data.data?.ads) ? res.data.data.ads : []);
      }
    } finally {
      setLoadingList(false);
    }
  }, [hitAxios]);

  const loadAd = React.useCallback(
    async (id) => {
      const res = await hitAxios({
        path: `/api/product-ads/${id}`,
        post: false,
        showLoading: false,
      });
      const row = readAdFromRes(res);
      if (row) {
        setAd(row);
        setJobProgress(row.job_progress || null);
        const mergedCfg = mergeAdConfig(row.config);
        setActiveStep(normalizeWizardStep(row.wizard_step));
        setProductLink(String(mergedCfg.product_url || ""));
        setCategoryId(row.cate_id != null ? String(row.cate_id) : "");
        setForm({
          product_name: row.product_name || "",
          description: row.description || "",
          selling_points: row.selling_points || "",
          cta: row.cta || "Shop now",
          script: row.script || "",
          config: mergedCfg,
        });
        if (row.media_url) setPreviewUrl(mediaSrc(row.media_url));
      }
      return row;
    },
    [hitAxios],
  );

  const runGenerationPoll = React.useCallback(
    async (maxTries = 120, { showPartialFailSnack = true, showDoneSnack = true } = {}) => {
      if (!adId || pollBusyRef.current) return;
      pollBusyRef.current = true;
      try {
        for (let i = 0; i < maxTries; i++) {
          // Poll more frequently early on (catching quick completions), then back off.
          const delay = i < 8 ? 2500 : i < 30 ? 4000 : 6000;
          await new Promise((r) => setTimeout(r, delay));
          const poll = await hitAxios({
            path: `/api/product-ads/${adId}/poll`,
            post: true,
            obj: {},
            showLoading: false,
          });
          const row = readAdFromRes(poll);
          const jp = poll?.data?.data?.job_progress || row?.job_progress;
          if (jp) setJobProgress(jp);
          if (row) setAd(row);
          const pct = jp?.progress_pct ?? Math.min(95, 10 + Math.round((i / maxTries) * 85));
          setPollPct(pct);

          const partialFail = jp?.status === "partial_failed" || jp?.failed_count > 0;
          if (partialFail && row?.status !== "completed") {
            if (showPartialFailSnack) {
              showSnack(
                lang?.productAdsSegmentRetryHint ||
                  "One or more clips failed. Tap Retry on the failed segment, then we will stitch the full ad.",
                false,
              );
            }
            return { done: true, completed: false, partialFail: true };
          }

          if (poll?.data?.data?.finalized || row?.status === "completed" || row?.status === "failed") {
            setPollPct(100);
            setJobProgress(jp || null);
            if (row?.status === "completed" && showDoneSnack) {
              showSnack(lang?.productAdsDone || "Your product ad is ready!", true);
              const vRes = await hitAxios({
                path: `/api/product-ads/${adId}/versions`,
                post: false,
                showLoading: false,
              });
              if (vRes?.data?.success) {
                setVersions(vRes.data.data?.versions || []);
              }
            }
            return { done: true, completed: row?.status === "completed", partialFail: false };
          }
        }
        return { done: false, completed: false, partialFail: false };
      } finally {
        pollBusyRef.current = false;
      }
    },
    [adId, hitAxios, lang, showSnack],
  );

  const refreshGeneration = React.useCallback(async () => {
    if (!adId) return;
    setRefreshingProgress(true);
    try {
      const poll = await hitAxios({
        path: `/api/product-ads/${adId}/poll`,
        post: true,
        obj: {},
        showLoading: false,
      });
      const row = readAdFromRes(poll);
      const jp = poll?.data?.data?.job_progress || row?.job_progress;
      if (jp) setJobProgress(jp);
      if (row) {
        setAd(row);
        setJobProgress(jp || row.job_progress || null);
      }
      if (poll?.data?.data?.finalized || row?.status === "completed") {
        setPollPct(100);
        if (row?.status === "completed") {
          showSnack(lang?.productAdsDone || "Your product ad is ready!", true);
        }
        return;
      }
      if (jp?.progress_pct != null) setPollPct(jp.progress_pct);
      if (row?.status === "generating" && !pollBusyRef.current) {
        setGenerating(true);
        await runGenerationPoll(80, { showPartialFailSnack: false, showDoneSnack: true });
      }
    } finally {
      setRefreshingProgress(false);
      setGenerating(false);
    }
  }, [adId, hitAxios, lang, runGenerationPoll, showSnack]);

  React.useEffect(() => {
    loadList();
    fetchCategories();
  }, [loadList, fetchCategories]);

  React.useEffect(() => {
    if (!adId || ad?.status !== "generating" || generating || pollBusyRef.current) return;
    let cancelled = false;
    (async () => {
      setGenerating(true);
      await runGenerationPoll(80, { showPartialFailSnack: false, showDoneSnack: true });
      if (!cancelled) setGenerating(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [adId, ad?.status, generating, runGenerationPoll]);

  const filteredAds = React.useMemo(() => {
    if (categoryFilter === "all") return ads;
    return ads.filter((item) => String(item.cate_id ?? "") === String(categoryFilter));
  }, [ads, categoryFilter]);

  React.useEffect(() => {
    if (!adId) return;
    let cancelled = false;
    (async () => {
      const res = await hitAxios({
        path: "/api/product-ads/meta",
        post: false,
        showLoading: false,
      });
      if (!cancelled && res?.data?.success) {
        const meta = res.data.data || {};
        setWizardMeta(meta);
        if (meta.showcase_available === false) {
          setForm((f) => {
            if (f.config?.generation_engine !== "veo_showcase") return f;
            return { ...f, config: { ...f.config, generation_engine: "heygen_agent", influencer_id: null } };
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adId, hitAxios]);

  React.useEffect(() => {
    return () => {
      if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  React.useEffect(() => {
    if (!adId) return;
    let cancelled = false;
    (async () => {
      setInfluencersLoading(true);
      setHeygenMetaLoading(true);
      try {
        const [infRes, vRes, aRes] = await Promise.all([
          fetchInfModels(hitAxios),
          hitAxios({ path: "/api/heygen/voices", post: false, showLoading: false }),
          hitAxios({ path: "/api/heygen/avatars", post: false, showLoading: false }),
        ]);
        if (cancelled) return;
        if (infRes?.data?.success) {
          const list = Array.isArray(infRes.data.data) ? infRes.data.data : [];
          setInfluencers(list.filter((m) => m.status === "active" && m.photo_url));
        }
        if (vRes?.data?.success) {
          const list = vRes.data.data?.voices ?? vRes.data.voices ?? [];
          setVoices(Array.isArray(list) ? list.slice(0, 80) : []);
        }
        if (aRes?.data?.success) {
          const list = aRes.data.data?.avatars ?? aRes.data.avatars ?? [];
          setAvatars(Array.isArray(list) ? list.slice(0, 48) : []);
        }
      } finally {
        if (!cancelled) {
          setInfluencersLoading(false);
          setHeygenMetaLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adId, hitAxios]);

  React.useEffect(() => {
    if (!adId || agentStyles.length) return;
    let cancelled = false;
    (async () => {
      setStylesLoading(true);
      try {
        const res = await hitAxios({
          path: "/api/heygen/video/agent/styles",
          post: false,
          showLoading: false,
        });
        if (cancelled) return;
        if (res?.data?.success && Array.isArray(res.data.data?.styles)) {
          setAgentStyles(
            res.data.data.styles.map(normalizeHeygenStyle).filter(Boolean).slice(0, 40),
          );
        }
      } finally {
        if (!cancelled) setStylesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeStep, agentStyles.length, hitAxios]);

  async function createNew() {
    const res = await hitAxios({
      path: "/api/product-ads",
      post: true,
      obj: { title: lang?.productAdsNewTitle || "New product ad" },
    });
    const row = readAdFromRes(res);
    if (row?.id) {
      setAdId(row.id);
      setAd(row);
      setActiveStep(0);
      setCategoryId(row.cate_id != null ? String(row.cate_id) : "");
      setForm({
        product_name: "",
        description: "",
        selling_points: "",
        cta: "Shop now",
        script: "",
        config: mergeAdConfig(row.config),
      });
      setPreviewUrl(null);
      setProductFile(null);
    }
  }

  async function persistPatch(patch, step) {
    if (!adId) return;
    setSaving(true);
    try {
      const res = await hitAxios({
        path: `/api/product-ads/${adId}`,
        method: "PATCH",
        obj: { ...patch, ...(step != null ? { wizard_step: step } : {}) },
        showLoading: false,
      });
      const row = readAdFromRes(res);
      if (row) setAd(row);
    } finally {
      setSaving(false);
    }
  }

  async function handleUpload() {
    if (!adId || !productFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", productFile);
      const res = await hitAxios({
        path: `/api/product-ads/${adId}/upload-media`,
        post: true,
        obj: fd,
        showLoading: false,
      });
      const row = readAdFromRes(res);
      if (row) {
        setAd(row);
        if (row.media_url) setPreviewUrl(mediaSrc(row.media_url));
        showSnack(lang?.productAdsUploadOk || "Product media uploaded", true);
      }
    } finally {
      setUploading(false);
    }
  }

  async function handleGenerateScript() {
    if (!adId) return;
    const curCfg = form.config || {};
    const link = String(productLink || curCfg.product_url || "").trim();
    if (!ad?.media_url && !link) {
      showSnack(
        lang?.productAdsScriptNeedSource ||
          "Upload a product image or enter a product link first, then generate the script.",
        false,
      );
      return;
    }
    if (link && link !== curCfg.product_url) {
      const nextCfg = { ...curCfg, product_url: link };
      setForm((f) => ({ ...f, config: nextCfg }));
      await persistPatch({ config: nextCfg });
    }
    setGeneratingScript(true);
    try {
      const res = await hitAxios({
        path: `/api/product-ads/${adId}/generate-script`,
        post: true,
        obj: {},
        showLoading: false,
      });
      if (res?.data?.success) {
        const d = res.data.data || {};
        const script = d.script ?? res.data.script;
        setForm((f) => ({
          ...f,
          script: script || f.script,
          product_name: d.product_name || f.product_name,
          description: d.description || f.description,
          selling_points: d.selling_points || f.selling_points,
          cta: d.cta || f.cta,
        }));
        const row = d.ad;
        if (row) {
          setAd(row);
        } else {
          setAd((a) =>
            a
              ? {
                  ...a,
                  script,
                  product_name: d.product_name || a.product_name,
                  description: d.description || a.description,
                  selling_points: d.selling_points || a.selling_points,
                  cta: d.cta || a.cta,
                }
              : a,
          );
        }
        showSnack(lang?.productAdsScriptOk || "Script generated from your product image", true);
      } else if (res?.data?.msg) {
        showSnack(res.data.msg, false);
      }
    } finally {
      setGeneratingScript(false);
    }
  }

  async function handleGenerate() {
    if (!adId) return;
    setGenerating(true);
    setPollPct(5);
    setJobProgress(null);
    try {
      await persistPatch(buildPersistPayload(form, productLink, categoryId), 3);

      const start = await hitAxios({
        path: `/api/product-ads/${adId}/generate`,
        post: true,
        obj: {},
        showLoading: false,
      });
      if (!start?.data?.success) {
        setGenerating(false);
        return;
      }
      const row = readAdFromRes(start);
      if (row) setAd(row);

      await runGenerationPoll(120);
    } finally {
      setGenerating(false);
    }
  }

  async function handleRegenerateSegment(segmentIndex) {
    if (!adId) return;
    setRegeneratingSegment(segmentIndex);
    try {
      const res = await hitAxios({
        path: `/api/product-ads/${adId}/regenerate-segment`,
        post: true,
        obj: { segment_index: segmentIndex },
        showLoading: false,
      });
      if (!res?.data?.success) return;
      const row = readAdFromRes(res);
      if (row) setAd(row);
      setJobProgress(res?.data?.data?.job_progress || row?.job_progress || null);
      showSnack(
        lang?.productAdsSegmentRetryStarted || `Clip ${segmentIndex + 1} is rendering again…`,
        true,
      );
      setGenerating(true);
      setPollPct(8);
      await runGenerationPoll(80);
    } finally {
      setRegeneratingSegment(null);
      setGenerating(false);
    }
  }

  async function handleDeleteAd(id, e) {
    if (e) e.stopPropagation();
    if (!window.confirm(lang?.productAdsDeleteConfirm || "Delete this ad and remove files from storage?")) {
      return;
    }
    const res = await hitAxios({
      path: `/api/product-ads/${id}/delete`,
      post: true,
      obj: {},
    });
    if (res?.data?.success) {
      showSnack(lang?.productAdsDeleted || "Ad deleted", true);
      if (adId === id) {
        setAdId(null);
        setAd(null);
      }
      loadList();
    }
  }

  async function handleGenerateVariants() {
    if (!adId) return;
    setGeneratingVariants(true);
    setVariantIds([]);
    try {
      const res = await hitAxios({
        path: `/api/product-ads/${adId}/generate-variants`,
        post: true,
        obj: { count: variantCount },
        showLoading: false,
      });
      if (res?.data?.success) {
        const ids = res.data.data?.variant_ids || [];
        setVariantIds(ids);
        showSnack(
          lang?.productAdsVariantsStarted ||
            `${ids.length} variants launched — check your ads list.`,
          true,
        );
      } else {
        showSnack(res?.data?.msg || "Variant generation failed", false);
      }
    } finally {
      setGeneratingVariants(false);
    }
  }

  function setConfig(key, value) {
    setForm((f) => ({ ...f, config: { ...f.config, [key]: value } }));
  }

  function validateStep(step) {
    if (step === 0) {
      if (!categoryId) return false;
      const hasSource =
        !!(ad?.media_url || previewUrl) || !!String(productLink || cfg.product_url || "").trim();
      const hasScript = !!String(form.script || "").trim();
      const engine = cfg.generation_engine || "veo_showcase";
      const hasCast = engine === "heygen_agent" ? true : !!cfg.influencer_id;
      return hasSource && hasScript && hasCast;
    }
    return true;
  }

  async function handleNext() {
    if (!validateStep(activeStep)) {
      showSnack(
        activeStep === 0 && !categoryId
          ? lang?.productAdsCategoryRequired || "Select a category first"
          : lang?.productAdsStepIncomplete || "Complete this step first",
        false,
      );
      return;
    }
    await persistPatch(buildPersistPayload(form, productLink, categoryId), activeStep + 1);
    setActiveStep((s) => Math.min(STEPS.length - 1, s + 1));
  }

  function handleBack() {
    setActiveStep((s) => Math.max(0, s - 1));
  }

  function closeWizard() {
    setAdId(null);
    setAd(null);
    loadList();
  }

  const cfg = form.config || {};
  const engine = cfg.generation_engine || "veo_showcase";
  const durationSec = normalizeProductAdDurationSec(cfg.duration_sec);
  const estimatedCredits = computeEstimatedCredits(durationSec, engine, wizardMeta);
  const selectedInfluencer = influencers.find((i) => Number(i.id) === Number(cfg.influencer_id));
  const selectedAvatar = avatars.find((a) => (a.avatar_id || a.id) === cfg.avatar_id);
  const selectedVoice = voices.find((v) => v.voice_id === cfg.voice_id);

  const pickerCardSx = {
    flex: 1,
    minWidth: { xs: "100%", sm: 0 },
    borderRadius: 2.5,
    p: 1.75,
    cursor: "pointer",
    textAlign: "left",
    border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
    bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.55 : 0.94),
    transition: "transform 0.2s ease, border-color 0.2s ease",
    "&:hover": {
      borderColor: alpha(theme.palette.primary.main, 0.45),
    },
  };

  if (!adId) {
    return (
      <Box>
        <PageHeader
          title={lang?.productAdsTitle || "Product Ads"}
          subtitle={
            lang?.productAdsSubtitle ||
            "Create professional product videos with AI avatars, scripts, and cinematic polish."
          }
          icon={CampaignOutlined}
        />
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button variant="contained" startIcon={<AutoAwesomeOutlined />} onClick={createNew}>
            {lang?.productAdsCreate || "Create product ad"}
          </Button>
        </Stack>
        {!loadingList && ads.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <CategoryFilterChips
              categories={categories}
              value={categoryFilter}
              onChange={setCategoryFilter}
              lang={lang}
            />
          </Box>
        )}
        {loadingList ? (
          <InlineLoader message={lang?.productAdsLoading || "Loading product ads"} />
        ) : ads.length === 0 ? (
          <Alert severity="info">
            {lang?.productAdsEmpty || "No product ads yet. Create one to start the wizard."}
          </Alert>
        ) : filteredAds.length === 0 ? (
          <Alert severity="info">
            {lang?.productAdsFilterEmpty || "No product ads in this category."}
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {filteredAds.map((item) => {
              const cover = adCoverSrc(item, influencers, item.config);
              return (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: "100%", position: "relative" }}>
                  <IconButton
                    size="small"
                    aria-label="delete"
                    onClick={(e) => handleDeleteAd(item.id, e)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 2,
                      bgcolor: alpha(theme.palette.background.paper, 0.85),
                      "&:hover": { bgcolor: "background.paper" },
                    }}
                  >
                    <DeleteOutlined fontSize="small" />
                  </IconButton>
                  <CardActionArea
                    onClick={() => {
                      setAdId(item.id);
                      loadAd(item.id);
                    }}
                  >
                    {cover ? (
                      <CardMedia
                        component="img"
                        height="140"
                        image={cover}
                        alt=""
                        sx={{ objectFit: "cover", bgcolor: "grey.900" }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 140,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CampaignOutlined color="disabled" sx={{ fontSize: 48 }} />
                      </Box>
                    )}
                    <CardContent>
                      <Typography fontWeight={700} gutterBottom noWrap>
                        {item.title || item.product_name || `Ad #${item.id}`}
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1, gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={item.status || "draft"}
                          color={item.status === "completed" ? "success" : item.status === "failed" ? "error" : "default"}
                        />
                        {item.category_name && (
                          <Chip
                            size="small"
                            label={item.category_name}
                            variant="outlined"
                            sx={
                              item.category_color
                                ? {
                                    borderColor: item.category_color,
                                    color: item.category_color,
                                  }
                                : undefined
                            }
                          />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.product_name || "—"}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
            })}
          </Grid>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <ChooseAvatarDialog
        open={picker === "avatar"}
        onClose={() => setPicker(null)}
        lang={lang}
        avatars={avatars}
        selectedLookId={cfg.avatar_id || ""}
        onPick={(row) => setConfig("avatar_id", row.avatar_id)}
      />
      <ChooseVoiceDialog
        open={picker === "voice"}
        onClose={() => setPicker(null)}
        lang={lang}
        voices={voices}
        selectedVoiceId={cfg.voice_id || ""}
        onPick={(id) => setConfig("voice_id", id)}
        showSnack={showSnack}
      />
      <ProductAdsInfluencerDialog
        open={picker === "creator"}
        onClose={() => setPicker(null)}
        lang={lang}
        influencers={influencers}
        loading={influencersLoading}
        selectedId={cfg.influencer_id}
        onPick={(id) => setConfig("influencer_id", id)}
      />

      <PageHeader
        title={lang?.productAdsWizardTitle || "Product ad studio"}
        subtitle={ad?.product_name || ad?.title || ""}
        icon={CampaignOutlined}
        secondaryActions={
          <Button size="small" onClick={closeWizard}>
            {lang?.productAdsBackList || "All ads"}
          </Button>
        }
      />

      <Stepper activeStep={activeStep} alternativeLabel={!isNarrow} sx={{ mb: 3 }}>
        {stepLabels.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent>
          {activeStep === 0 && (
            <ProductAdsStudioStep
              lang={lang}
              theme={theme}
              pickerCardSx={pickerCardSx}
              engine={engine}
              wizardMeta={wizardMeta}
              GENERATION_ENGINES={GENERATION_ENGINES}
              productFile={productFile}
              uploading={uploading}
              previewUrl={previewUrl}
              ad={ad}
              productLink={productLink}
              setProductLink={setProductLink}
              setProductFile={setProductFile}
              setPreviewUrl={setPreviewUrl}
              handleUpload={handleUpload}
              setConfig={setConfig}
              setPicker={setPicker}
              selectedInfluencer={selectedInfluencer}
              selectedAvatar={selectedAvatar}
              selectedVoice={selectedVoice}
              generatingScript={generatingScript}
              handleGenerateScript={handleGenerateScript}
              form={form}
              setForm={setForm}
              cfg={cfg}
              isNarrow={isNarrow}
              categories={categories}
              categoriesLoading={categoriesLoading}
              categoryId={categoryId}
              onCategoryChange={setCategoryId}
            />
          )}

          {activeStep === 1 && (
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel>{lang?.productAdsFormat || "Ad format"}</InputLabel>
                <Select
                  label={lang?.productAdsFormat || "Ad format"}
                  value={cfg.video_format || "ugc_shorts"}
                  onChange={(e) => setConfig("video_format", e.target.value)}
                >
                  {FORMAT_OPTIONS.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {lang?.[f.labelKey] || f.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {(cfg.video_format || "ugc_shorts") === "ugc_shorts" && (
                <Alert severity="info" icon={false}>
                  {lang?.productAdsUgcFormatHint ||
                    "Optimized for Shorts: casual talking-head, product close-ups, jump cuts, bold captions (10–60s)."}
                </Alert>
              )}
              <Typography variant="subtitle2">
                {lang?.productAdsDuration || "Target length"}
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={durationSec}
                onChange={(_, v) => v != null && setConfig("duration_sec", v)}
                size="small"
              >
                {(wizardMeta.duration_options || DURATION_OPTIONS).map((d) => (
                  <ToggleButton key={d} value={d}>
                    {d}s
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Alert
                severity={
                  estimatedCredits > 0 && wizardMeta.user_credits != null && wizardMeta.user_credits < estimatedCredits
                    ? "warning"
                    : "info"
                }
                icon={false}
                sx={{ mt: 1 }}
              >
                {wizardMeta.pricing_mode === "per_second" && wizardMeta.credits_per_second > 0 ? (
                  <>
                    {lang?.productAdsPricePerSecond ||
                      "Estimated cost:"}{" "}
                    <strong>{estimatedCredits}</strong>{" "}
                    {lang?.credits || "credits"}{" "}
                    ({durationSec}s × {wizardMeta.credits_per_second}{" "}
                    {lang?.productAdsPerSec || "per second"})
                    {wizardMeta.user_credits != null && (
                      <>
                        {" "}
                        · {lang?.productAdsYourBalance || "Your balance"}: {wizardMeta.user_credits}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {lang?.productAdsFlatPrice || "Estimated cost:"}{" "}
                    <strong>{estimatedCredits}</strong> {lang?.credits || "credits"}
                    {wizardMeta.user_credits != null && (
                      <> · {lang?.productAdsYourBalance || "Your balance"}: {wizardMeta.user_credits}</>
                    )}
                  </>
                )}
              </Alert>
              {(cfg.generation_engine || "veo_showcase") === "veo_showcase" && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {lang?.productAdsShowcaseDurationHint ||
                    "Longer videos are built from several clips stitched together (up to 60s)."}
                </Typography>
              )}
              <Typography variant="subtitle2">
                {lang?.productAdsAspect || "Aspect ratio"}
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={cfg.aspect_ratio || "9:16"}
                onChange={(_, v) => v && setConfig("aspect_ratio", v)}
                size="small"
              >
                {ASPECT_OPTIONS.map((a) => (
                  <ToggleButton key={a} value={a}>
                    {a}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <FormControl fullWidth>
                <InputLabel>{lang?.productAdsBackground || "Background"}</InputLabel>
                <Select
                  label={lang?.productAdsBackground || "Background"}
                  value={cfg.background_style || "lifestyle"}
                  onChange={(e) => setConfig("background_style", e.target.value)}
                >
                  {BG_STYLES.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{lang?.productAdsHeygenStyle || "Visual style (HeyGen)"}</InputLabel>
                <Select
                  label={lang?.productAdsHeygenStyle || "Visual style (HeyGen)"}
                  value={cfg.style_id || ""}
                  onChange={(e) => setConfig("style_id", e.target.value || null)}
                  disabled={stylesLoading}
                >
                  <MenuItem value="">
                    {lang?.productAdsAuto || "Auto (HeyGen picks)"}
                  </MenuItem>
                  {agentStyles.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{lang?.productAdsMusic || "Music"}</InputLabel>
                <Select
                  label={lang?.productAdsMusic || "Music"}
                  value={cfg.music_track || "ugc_upbeat"}
                  onChange={(e) => setConfig("music_track", e.target.value)}
                >
                  {MUSIC_OPTIONS.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {lang?.[m.labelKey] || m.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={cfg.subtitles !== false}
                    onChange={(e) => setConfig("subtitles", e.target.checked)}
                  />
                }
                label={lang?.productAdsSubtitles || "Auto subtitles"}
              />
              {(cfg.generation_engine || "veo_showcase") === "heygen_agent" && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={cfg.seedance_enabled !== false}
                      onChange={(e) => setConfig("seedance_enabled", e.target.checked)}
                    />
                  }
                  label={
                    <Tooltip title={lang?.productAdsSeedanceTip || "Cinematic motion via Seedance when configured"}>
                      <span>{lang?.productAdsSeedance || "Seedance motion polish"}</span>
                    </Tooltip>
                  }
                />
              )}
              <FormControl fullWidth>
                <InputLabel>{lang?.productAdsExport || "Export quality"}</InputLabel>
                <Select
                  label={lang?.productAdsExport || "Export quality"}
                  value={cfg.export_quality || "1080p"}
                  onChange={(e) => setConfig("export_quality", e.target.value)}
                >
                  <MenuItem value="720p">720p HD</MenuItem>
                  <MenuItem value="1080p">1080p Full HD</MenuItem>
                  <MenuItem value="4k">4K (when supported)</MenuItem>
                </Select>
              </FormControl>
              <Stack direction="row" spacing={2}>
                <TextField
                  label={lang?.productAdsPrimaryColor || "Brand primary"}
                  type="color"
                  value={cfg.brand_colors?.primary || "#00C853"}
                  onChange={(e) =>
                    setConfig("brand_colors", {
                      ...cfg.brand_colors,
                      primary: e.target.value,
                    })
                  }
                  sx={{ width: 120 }}
                />
                <TextField
                  label={lang?.productAdsLogoUrl || "Logo URL (optional)"}
                  fullWidth
                  value={cfg.logo_url || ""}
                  onChange={(e) => setConfig("logo_url", e.target.value)}
                />
              </Stack>
            </Stack>
          )}

          {activeStep === 2 && (
            <Stack spacing={2}>
              <Alert severity="info">
                {lang?.productAdsPreviewNote ||
                  "Review your settings. The final video uses your script, avatar, and branding."}
              </Alert>
              <Typography variant="body2">
                <strong>{form.product_name}</strong> — {form.cta}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                {form.script?.slice(0, 400)}
                {(form.script?.length || 0) > 400 ? "…" : ""}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={
                    cfg.generation_engine === "heygen_agent"
                      ? lang?.productAdsEngineHeygen || "HeyGen avatar"
                      : lang?.productAdsEngineVeo || "Creator + product"
                  }
                  size="small"
                  color="primary"
                />
                <Chip
                  label={
                    cfg.video_format === "studio"
                      ? lang?.productAdsFormatStudio || "Studio"
                      : lang?.productAdsFormatUgc || "UGC Shorts"
                  }
                  size="small"
                />
                <Chip label={`${durationSec}s · ${estimatedCredits} cr`} size="small" color="secondary" />
                <Chip label={cfg.aspect_ratio || "9:16"} size="small" />
                <Chip label={cfg.background_style || "lifestyle"} size="small" />
                {cfg.seedance_enabled !== false && (
                  <Chip label="Seedance" size="small" color="primary" variant="outlined" />
                )}
              </Stack>
              {ad?.preview_video || ad?.generated_video ? (
                <Box
                  component="video"
                  controls
                  src={mediaSrc(ad.generated_video || ad.preview_video)}
                  sx={{ width: "100%", maxHeight: 360, borderRadius: 2, bgcolor: "#000" }}
                />
              ) : null}
            </Stack>
          )}

          {activeStep === 3 && (
            <Stack spacing={2} alignItems="center">
              {estimatedCredits > 0 && (
                <Alert severity="info" sx={{ width: "100%" }}>
                  {lang?.productAdsChargeNotice ||
                    "Generating will charge"}{" "}
                  <strong>{estimatedCredits}</strong> {lang?.credits || "credits"} ({durationSec}s)
                </Alert>
              )}
              {(generating || ad?.status === "generating") && (
                <Stack spacing={2} sx={{ width: "100%", maxWidth: 480 }}>
                  <Typography variant="body1" fontWeight={600} textAlign="center">
                    {formatGenerationStatus(lang, ad, jobProgress, pollPct)}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, jobProgress?.progress_pct ?? pollPct)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  {(jobProgress?.segments || ad?.job_progress?.segments)?.length > 0 && (
                    <Stack spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        {lang?.productAdsClipsPlan ||
                          `Plan: ${jobProgress?.duration_plan || ad?.job_progress?.duration_plan || ""} → one final video`}
                      </Typography>
                      {(jobProgress?.segments || ad?.job_progress?.segments).map((seg) => (
                        <Stack
                          key={seg.index}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                          }}
                        >
                          <Chip
                            size="small"
                            label={`${lang?.productAdsClip || "Clip"} ${seg.index + 1} · ${seg.duration_sec}s`}
                            color={
                              seg.status === "complete"
                                ? "success"
                                : seg.status === "failed"
                                  ? "error"
                                  : "default"
                            }
                          />
                          <Typography variant="caption" sx={{ flex: 1 }} color="text.secondary" noWrap>
                            {seg.status === "complete"
                              ? lang?.productAdsClipDone || "Ready"
                              : seg.status === "failed"
                                ? seg.error || lang?.productAdsClipFailed || "Failed"
                                : lang?.productAdsClipRendering || "Rendering…"}
                          </Typography>
                          {seg.status === "failed" && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={regeneratingSegment != null}
                              onClick={() => handleRegenerateSegment(seg.index)}
                            >
                              {regeneratingSegment === seg.index ? (
                                <CircularProgress size={16} />
                              ) : (
                                lang?.productAdsRetryClip || "Retry"
                              )}
                            </Button>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Stack>
              )}
              {ad?.status === "completed" && ad?.generated_video && (
                <>
                  <Alert severity="success" sx={{ width: "100%" }}>
                    {lang?.productAdsReady || "Video ready"}
                  </Alert>
                  {(ad.cover_image || ad.video_thumbnail) && (
                    <Box
                      component="img"
                      src={ad.cover_image || mediaSrc(ad.video_thumbnail)}
                      alt=""
                      sx={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 2 }}
                    />
                  )}
                  <Box
                    component="video"
                    controls
                    poster={ad.cover_image || mediaSrc(ad.video_thumbnail) || undefined}
                    src={mediaSrc(ad.generated_video)}
                    sx={{ width: "100%", maxHeight: 400, borderRadius: 2, bgcolor: "#000" }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<DownloadOutlined />}
                    href={mediaSrc(ad.generated_video)}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {lang?.productAdsDownload || "Download video"}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ScheduleSendOutlined />}
                    onClick={() => {
                      const params = new URLSearchParams(window.location.search);
                      params.set("page", "social-scheduler");
                      window.history.pushState({}, "", `${window.location.pathname}?${params}`);
                      window.dispatchEvent(new PopStateEvent("popstate"));
                    }}
                  >
                    {lang?.productAdsSchedule || "Schedule post"}
                  </Button>
                </>
              )}
              {ad?.status === "completed" && ad?.generated_video && (
                <Card variant="outlined" sx={{ width: "100%", mt: 2, borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ContentCopyOutlined fontSize="small" />
                      {lang?.productAdsVariantsTitle || "Generate A/B variants"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                      {lang?.productAdsVariantsHint ||
                        "Each variant uses a different hook and CTA — test them in your ad manager."}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Variants</InputLabel>
                        <Select
                          label="Variants"
                          value={variantCount}
                          onChange={(e) => setVariantCount(Number(e.target.value))}
                        >
                          <MenuItem value={2}>2</MenuItem>
                          <MenuItem value={3}>3</MenuItem>
                          <MenuItem value={4}>4</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                          generatingVariants ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <AutoAwesomeOutlined />
                          )
                        }
                        onClick={handleGenerateVariants}
                        disabled={generatingVariants}
                      >
                        {lang?.productAdsGenerateVariants || "Generate variants"}
                      </Button>
                    </Stack>
                    {variantIds.length > 0 && (
                      <Stack direction="row" flexWrap="wrap" gap={0.8} sx={{ mt: 1.5 }}>
                        {variantIds.map((vid, i) => (
                          <Chip
                            key={vid}
                            label={`Variant ${i + 1} (#${vid})`}
                            size="small"
                            clickable
                            component="a"
                            href={`?page=product-ads&adId=${vid}`}
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              )}
              {ad?.status === "failed" && (
                <Alert severity="error" sx={{ width: "100%" }}>
                  {ad.error_message || lang?.productAdsFailed || "Generation failed"}
                </Alert>
              )}
              {jobProgress?.failed_count > 0 && ad?.status !== "completed" && !generating && (
                <Alert severity="warning" sx={{ width: "100%" }}>
                  {lang?.productAdsPartialFailBanner ||
                    "Some clips failed. Retry each failed clip below, then generation will continue automatically."}
                </Alert>
              )}
              {!generating && ad?.status !== "completed" && ad?.status !== "generating" && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrowOutlined />}
                  onClick={handleGenerate}
                  disabled={ad?.status === "generating"}
                >
                  {lang?.productAdsStartGen || "Generate video"}
                </Button>
              )}
              {versions.length > 0 && (
                <Stack spacing={1} sx={{ width: "100%", mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <HistoryOutlined fontSize="small" />
                    {lang?.productAdsVersions || "Version history"}
                  </Typography>
                  {versions.map((v) => (
                    <Chip
                      key={v.id}
                      label={`v${v.version_number} · ${v.status}`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Stack direction="row" justifyContent="space-between">
        <Button disabled={activeStep === 0 || saving} onClick={handleBack}>
          {lang?.back || "Back"}
        </Button>
        {activeStep < STEPS.length - 1 ? (
          <Button variant="contained" disabled={saving} onClick={handleNext}>
            {saving ? <CircularProgress size={22} color="inherit" /> : lang?.next || "Next"}
          </Button>
        ) : (
          <Button
            variant="outlined"
            startIcon={refreshingProgress ? <CircularProgress size={18} /> : <RefreshOutlined />}
            disabled={refreshingProgress || generating}
            onClick={() =>
              ad?.status === "generating" || generating
                ? refreshGeneration()
                : loadAd(adId)
            }
          >
            {ad?.status === "generating" || generating
              ? lang?.productAdsCheckProgress || "Check progress"
              : lang?.refresh || "Refresh"}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default ProductAds;
